import Flex from "@/components/Flex/Flex";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import { useEffect, useState } from "react";
import { useShipmentContext } from "@/contexts/ShipmentContext";
import IconInfo from "@/components/icons/IconInfo";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import Button from "@/components/forms/Button/Button";
import Alert from "@/components/Alert/Alert";

import styles from "./CloneShipmentModal.module.css";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { FbaBatchStatus } from "@boxem/core-api-schema";
import { useLocation } from "wouter";


export interface CloneShipmentModalProps {
  open: boolean;
  focusedShipmentId?: number;
  onClose: () => void;
}

export function CloneShipmentModal(props: CloneShipmentModalProps) {
  const { open, onClose, focusedShipmentId } = props;

  const {currentBatch, cloneShipment} = useShipmentContext();
  const {handleErrorResponseGenerically} = useErrorModalContext();

  const [cancelShipment, setCancelShipment] = useState<boolean>(true);
  const [cloneAllSKUs, setCloneAllSKUs] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [location, navigate] = useLocation();

  useEffect(() => {
    // ensure you don't get the option to cancel a batch that is already cancelled:
    if (currentBatch?.status === FbaBatchStatus.BATCH_CANCELLED) {
      setCancelShipment(false);
    }
  }, [currentBatch]);

  const hasMultipleShipments = currentBatch?.shipments?.length > 1;

  /* @TODO(michaelp): Make this a growFromTop modal once box templates is merged with that prop */
  return (

    <Modal
      open={open}
      onOpenChange={o => { if (!o && !loading) onClose(); }}
      style={{width: "50rem"}}
    >
      <ModalHeader divider={false}>
        <center>
          <div style={{display:"block"}}>
            <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="70" height="70" rx="35" fill="#1258D2"/>
            <path d="M31.5 45.5C30.5375 45.5 29.7135 45.1573 29.0281 44.4719C28.3427 43.7865 28 42.9625 28 42V21C28 20.0375 28.3427 19.2135 29.0281 18.5281C29.7135 17.8427 30.5375 17.5 31.5 17.5H47.25C48.2125 17.5 49.0365 17.8427 49.7219 18.5281C50.4073 19.2135 50.75 20.0375 50.75 21V42C50.75 42.9625 50.4073 43.7865 49.7219 44.4719C49.0365 45.1573 48.2125 45.5 47.25 45.5H31.5ZM31.5 42H47.25V21H31.5V42ZM24.5 52.5C23.5375 52.5 22.7135 52.1573 22.0281 51.4719C21.3427 50.7865 21 49.9625 21 49V24.5H24.5V49H43.75V52.5H24.5Z" fill="white"/>
            </svg>
          </div>
          <h3>Clone Shipment</h3>
        </center>
      </ModalHeader>
      <ModalBody>

        <Flex direction="column" justify="start" align="start" gap={2} className={styles.body}>
          {loading && (
            <>
              <p>Working on it...</p>
              <LoadingSpinner/>
            </>
          )}
          {!loading && (
            <Flex gap={4} direction="column" align="start">
              <p>Cloning a shipment creates a new shipment with the same SKUs and quantities, and puts you back in Step 1. It's useful when you want to re-make this shipment but with minor changes.</p>
              
              <Flex gap={2} direction="column" style={{alignSelf: "stretch"}}>
                {hasMultipleShipments && (
                  <label className={styles.label}>
                    <Checkbox
                      value={cloneAllSKUs}
                      onChange={v => setCloneAllSKUs(v)}
                    />
                    <span>Clone all SKUs</span>
                    <Tooltip title={"If enabled, the cloned shipment will contain SKUs from all shipments in your selected placement option, not just the one currently selected"}>
                      <IconInfo/>
                    </Tooltip>
                  </label>
                )}

                {currentBatch?.status !== FbaBatchStatus.BATCH_CANCELLED && (
                  <label className={styles.label}>
                    <Checkbox
                      value={cancelShipment}
                      onChange={v => setCancelShipment(v)}
                    />
                    <span>Cancel current shipment?</span>
                  </label>
                )}

                {(cancelShipment) &&
                  <Alert fullWidth={true} type="error" message={hasMultipleShipments ? "All of the shipments for your selected placement option will be cancelled (not just the one currently selected!) This will permanently erase all progress. Please make sure this is what you want before proceeding." : "Canceling this shipment will permanently erase all progress. Please make sure this is what you want before proceeding."}/>
                }

              </Flex>
            </Flex>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justify="space-between" gap={2}>
          <Button
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            type={cancelShipment ? "danger" : "primary"}
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              try {
                const {id} = await cloneShipment(cancelShipment, cloneAllSKUs ? null : focusedShipmentId);
                navigate(`/fba/shipments/${id}`);
              } catch (e) {
                handleErrorResponseGenerically(e);
              } finally {
                setLoading(false);
              }
              onClose();
            }}
          >
            {cancelShipment ? "Cancel and Clone Current Shipment" : "Clone Shipment"}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}